<script setup lang="ts">
import {
    faArrowLeft,
    faArrowRight,
    faArrowRotateLeft,
    faMoneyBillTransfer,
    faWallet,
} from '@fortawesome/pro-regular-svg-icons';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import TheAutoReserveFlightSvg from '@files/auto-reserve/flight-modal.svg?component';

const emit = defineEmits<{
    close: [args?: { silentClose?: boolean }];
}>();
const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
const router = useRouter();
const showMore = ref(false);
const isIphone = ref(false);
const searchFormStore = useSearchFormStore();
const route = useRoute();
const { reserveType } = storeToRefs(searchFormStore);
const { login } = useAuth();
const reserveName = computed(() =>
    reserveType.value === 'flight' ? 'پرواز' : 'اتوبوس'
);
const proceedText = computed(() =>
    authStore.loggedIn
        ? `ادامه و انتخاب ${reserveName.value}‌ها`
        : 'ادامه و ورود'
);
const rules = [
    {
        head: 'قوانین رزرو خودکار',
        body: 'کاربر گرامی؛ درصورتی که اولین بار است که از سرویس رزرو خودکار استفاده می‌کنید، به دلیل تفاوت در ساز و کار این نوع رزرو با دیگر فرآیندهای رزرو، لازم است شرایط و مقررات سرویس را مطالعه و مورد توجه قرار دهید.',
    },
    {
        head: 'مبلغ پرداختی به اعتبار کاربری شما افزوده می‌شود.',
        body: 'مبلغی که در انتها برای این رزرو پرداخت می‌کنید، سپرده شما نزد ماست و شما می‌توانید هر زمانی که مایل باشید کل و یا بخشی از آن را به حساب یا کارت بانکی استرداد کنید. استرداد اعتبار در اکثر موارد به‌صورت آنی بوده و در موارد خاص حداکثر تا سه روز کاری زمان خواهد برد. تنها زمانی که بلیط به‌صورت قطعی صادر شود این مبلغ از اعتبار کاربری شما کسر می‌شود. توجه داشته باشید، درصورتی‌که هنگام موجود شدن ظرفیت اعتبار شما کافی نباشد، رزرو شما لغو خواهد شد.',
    },
    {
        head: 'به محض موجود شدن ظرفیت، فرآیند خرید به صورت خودکار انجام می‌شود.',
        body: 'پس از انتخاب سفرها و ساعت آن (پرواز، اتوبوس و...)، سیستم رزرو خودکار مِستربلیط، به‌صورت مداوم اقدام به بررسی ظرفیت سفرها خواهد کرد و در صورت آزاد شدن ظرفیت مورد نظر، اقدام به خریداری خودکار بلیط می‌کند. در صورت انتخاب چندین سفر، تنها یکی از آنان (هر سفری که موجود شود) برای شما خریداری می‌گردد. همچنین کل تعداد بلیط درخواستی شما روی یک سفر (زمان و سرویس مشخص) صادر می‌شود و امکان صدور روی چند سفر وجود ندارد. در ضمن، امکان ساخت چند رزرو خودکار روی یک سفر وجود خواهد داشت که هر کدام به‌صورت مجزا عمل کرده و صادر می‌شوند.',
    },
    {
        head: 'قبل از صدور قطعی، می‌توانید بدون جریمه انصراف دهید.',
        body: 'تا زمانی که بلیط واقعاً توسط سیستم صادر نشده است، می‌توانید با مراجعه به صفحه رزرو خودکار در پنل کاربری، (قابل دسترسی در منوی مدیریت کاربری) رزرو خودکار خود را لغو کنید. پس از این کار دیگر بلیط برایتان صادر نمی‌شود و می‌توانید مبلغ پرداختی را از اعتبار کاربری به حساب یا کارت بانکی استرداد کنید. توجه داشته باشید اگر از طریق دیگر قبل از قطعی شدن رزرو موفق به تهیه بلیط شدید، باید این رزرو را لغو کنید، وگرنه دو بلیط خواهید داشت که باید یکی را با پرداخت جریمه کنسل کنید!',
    },
    {
        head: 'اگر رزرو قطعی شد، کنسلی و انصراف از آن مشمول جریمه است.',
        body: 'تا قبل از این‌که رزرو برای شما نهایی شود شما می‌توانید هر زمانی که مایل باشید بدون پرداخت هزینه‌ای درخواست رزرو خودکار خود را لغو کنید. اما به محض صدور بلیط، کنسلی آن مطابق قوانین تأمین‌کننده مربوطه مشمول جریمه خواهد بود. میزان جریمه بنا به تأمین‌کننده و زمان باقی‌مانده تا سفر متغیر خواهد بود و حتی ممکن است غیرقابل استرداد باشد. توضیح بیشتر این‌که کنسلی بلیط صادرشده در اکثر موارد از سمت تأمین‌کننده (ایرلاین، چارترکننده و تعاونی) مشمول جریمه است و جریمه مذکور از سمت مِستربلیط اعمال نمی‌شود، لذا (بر خلاف خواسته قلبی ما!) کنسلی بدون جریمه آن از عهده و اختیار مِستربلیط خارج خواهد بود.',
    },
    {
        head: 'سه ساعت قبل از سفر، جستجو پایان می‌یابد.',
        body: 'رزرو خودکار ظرفیت سفر را تا سه ساعت قبل از آن رهگیری می‌کند و درصورت موجود شدن، بلیط برای شما خریداری می‌شود. بعد از این زمان امکان موجود شدن ظرفیت برای این سفر به‌صورت آنلاین وجود ندارد. اگر سفر خیلی ضروری دارید می‌توانید بعد از این زمان شانس را خود برای خرید بلیط در فرودگاه یا ترمینال نیز امتحان کنید.',
    },
    {
        head: 'مبلغ بلیط صادر شده ممکن است کمتر باشد.',
        body: 'مبلغ بلیط در لحظه ساخت رزرو مشخص نیست و ما سقف مبلغ این مسیر را از شما دریافت می‌کنیم (براساس قوانین سازمان‌های مربوطه، مبلغ بلیط از سقف مشخصی نمی‌تواند بیشتر باشد). اگر بلیط با قیمت کمتری صادر شد، باقی‌مانده پول در اعتبار شما موجود خواهد بود و می‌توانید آن را استرداد کنید.',
    },
    {
        head: 'مبلغ خدمات رزرو جدای از مبلغ بلیط دریافت می‌شود.',
        body: 'بنا به سطح کاربری و سابقه خریدهای شما، ممکن است مبلغی به‌عنوان هزینه خدمات رزرو خودکار از شما دریافت شود. این مبلغ در صورت‌حساب به شما نمایش داده می‌شود. اگر بلیطی برای شما صادر نشد، هیچ هزینه‌ای (چه هزینه بلیط و چه هزینه خدمات) از شما دریافت نمی‌شود، ولی درصورت صدور بلیط، مبلغ مذکور به‌عنوان هزینه استفاده از سرویس رزرو خودکار از شما دریافت می‌شود و این مبلغ جدای از مبلغ بلیط شما خواهد بود. درصورتی که رزرو خودکار با موفقیت انجام و سفر به هر دلیل کنسل شود، هزینه خدمات رزرو خودکار برای مسافر استرداد نخواهد شد.',
    },
    {
        head: 'صدور قطعی و یا تغییر در وضعیت رزرو خودکار، به صورت پیامک، ایمیل و یا تماس خودکار اطلاع رسانی می‌شود.',
        body: 'البته ممکن است بخواهید از طریق دیگر نیز شانس خود را برای خرید بلیط امتحان کنید، لذا پیش از نهایی کردن خرید از مسیرهای دیگر، حتماً وضعیت رزرو خود را بررسی کنید، چون ممکن است به‌دلیل اختلالات مخابراتی پیامک صدور دیرتر برایتان ارسال شود.',
    },
];
const closeModal = (silent?: boolean) => {
    showMore.value = false;
    emit('close', { silentClose: silent });
};
const proceed = async () => {
    const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
    analyticsStore.trackAll('auto-reserve-modal-button-click', {
        loggedIn: authStore.loggedIn,
        from: currentSearchForm.value?.origin?.title,
        to: currentSearchForm.value?.destination?.title,
        adultCount: currentSearchForm.value?.adultCount,
        creationSource: authStore.siteSourceId,
        reserveType: reserveType.value,
        tripType: currentSearchForm.value?.tripType,
        link: route.fullPath,
    });
    const loggedIn = await login({
        message: 'برای استفاده از رزرو خودکار باید ابتدا وارد شوید.',
    });
    if (loggedIn) {
        closeModal(true);
        analyticsStore.trackAll('auto-reserve-proceed-to-cart', {
            from: currentSearchForm.value?.origin?.title,
            to: currentSearchForm.value?.destination?.title,
            adultCount: currentSearchForm.value?.adultCount,
            creationSource: authStore.siteSourceId,
            reserveType: reserveType.value,
            tripType: currentSearchForm.value?.tripType,
            link: route.fullPath,
        });
        router.push('/reserve/auto');
    }
};

onMounted(() => {
    isIphone.value =
        [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod',
        ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document) ||
        navigator.userAgent.includes('Safari');
});
</script>
<template>
    <mr-modal
        title="سرویس رزرو خودکار"
        width="500px"
        mobile-page
        mobile-page-header-color="var(--blue-400)"
        mobile-header-text-color="white"
        @close="closeModal(false)"
    >
        <section class="details-section">
            <header class="tabs-header" :class="{ 'iphone-header': isIphone }">
                <button
                    class="tab"
                    :class="{ selected: !showMore }"
                    @click="showMore = false"
                >
                    چرا رزرو خودکار؟
                </button>
                <button
                    class="tab"
                    :class="{ selected: showMore }"
                    @click="showMore = true"
                >
                    قوانین رزرو خودکار
                </button>
            </header>
            <mr-divider :thickness="1" />
            <div class="tab-contents-wrapper">
                <transition>
                    <keep-alive class="tab-content-container">
                        <div v-if="!showMore" class="modal-content-wrapper">
                            <TheAutoReserveFlightSvg
                                v-if="reserveType === 'flight'"
                                class="flight"
                            />
                            <img
                                v-else-if="reserveType === 'bus'"
                                class="flight"
                                src="~/assets/files/auto-reserve/bus-modal.png"
                                alt="bus"
                            />
                            <mr-typography weight="700">
                                نگران پیدا نشدن {{ reserveName }} هستید؟
                            </mr-typography>
                            <mr-typography class="body">
                                با رزرو خودکار، به محض موجود شدن ظرفیت هر یک از
                                {{ reserveName }}های انتخابی به شرط داشتن شرایط
                                ای که شما تعیین میکنید، به صورت آنی و بدون نیاز
                                به اقدام شما {{ reserveName }} مورد نظرتان رزرو
                                خواهد شد.
                            </mr-typography>
                            <div class="rule-container">
                                <mr-icon
                                    :icon="faWallet"
                                    color="var(--blue-300)"
                                />
                                <mr-typography
                                    weight="500"
                                    color="var(--cool-gray-700)"
                                >
                                    جهت رزرو {{ reserveName }}، می‌بایست موجودی
                                    اعتبار کاربری خود را به اندازه سقف قیمتی
                                    {{ reserveName }} افزایش دهید.
                                </mr-typography>
                            </div>
                            <div class="rule-container">
                                <mr-icon
                                    :icon="faMoneyBillTransfer"
                                    color="var(--blue-300)"
                                />
                                <mr-typography
                                    weight="500"
                                    color="var(--cool-gray-700)"
                                >
                                    اگر {{ reserveName }} انتخابی شما موجود نشد،
                                    کل مبلغ پرداختی برای هزینه خدمات و
                                    {{ reserveName }}
                                    به‌طور قابل بازگشت خواهدبود.
                                </mr-typography>
                            </div>
                            <div class="rule-container">
                                <mr-icon
                                    :icon="faArrowRotateLeft"
                                    color="var(--blue-300)"
                                />
                                <mr-typography
                                    weight="500"
                                    color="var(--cool-gray-700)"
                                >
                                    در هر لحظه، مبلغ سپرده موجود در اعتبار
                                    کاربری، قابل بازگشت به حساب بانکی شماست.
                                </mr-typography>
                            </div>
                        </div>
                    </keep-alive>
                </transition>
                <transition>
                    <keep-alive class="tab-content-container">
                        <div v-if="showMore" class="modal-rules-content">
                            <template
                                v-for="(rule, index) in rules"
                                :key="`head-${index}`"
                            >
                                <mr-typography
                                    size="large"
                                    weight="700"
                                    class="head"
                                >
                                    <span v-if="index" class="circle-1" />
                                    <span v-if="index" class="circle-2" />
                                    {{ rule.head }}
                                </mr-typography>
                                <mr-typography size="large" class="body">
                                    {{ rule.body }}
                                </mr-typography>
                            </template>
                        </div>
                    </keep-alive>
                </transition>
            </div>
        </section>
        <template #footer>
            <div class="footer-buttons" :class="{ 'iphone-footer': isIphone }">
                <mr-button
                    variant="transparent"
                    full-width
                    :left-icon="!showMore ? faArrowLeft : undefined"
                    :right-icon="showMore ? faArrowRight : undefined"
                    @click="showMore = !showMore"
                >
                    {{ showMore ? 'بازگشت' : 'مطالعه قوانین' }}
                </mr-button>
                <mr-button full-width @click.exact="proceed()">
                    {{ proceedText }}
                </mr-button>
            </div>
        </template>
    </mr-modal>
</template>

<style lang="scss" scoped>
.iphone-header {
    @include mobile-only {
        padding-top: 40px !important;
    }
}
.iphone-footer {
    @include mobile-only {
        padding-bottom: 40px !important;
    }
}
.details-section {
    flex: 1 1 auto;
    @include v-transition-horizontal-paging();
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .tabs-header {
        display: flex;
        padding-right: 20px;
        flex-shrink: 0;
        @include mobile-only {
            @include no-scrollbar;
            padding: 0 16px 0 0;
            overflow-x: scroll;
        }
        .tab {
            all: unset;
            @include tg-m($w5, usePalette($p-cool-gray, $p-600));
            padding: 7px 16px;
            margin-left: 4px;
            border-bottom-width: 2px;
            border-bottom-color: transparent;
            border-bottom-style: solid;
            cursor: pointer;
            white-space: nowrap;
            flex-basis: 50%;
            text-align: center;
            &.selected {
                color: usePalette();
                border-bottom-color: usePalette();
            }
        }
    }

    .tab-contents-wrapper {
        @include no-scrollbar;
        display: flex;
        width: 100%;
        overflow-x: hidden;
        @include mobile-only {
            overflow-y: scroll;
        }
        .tab-content-container {
            min-width: 100%;
        }
    }
}
.modal-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 500px;
    padding: 20px;
    @include mobile-only {
        max-width: 100%;
    }
    .flight {
        max-height: 150px;
        align-self: center;
        flex-shrink: 0;
        @include mobile-only {
            max-width: 100%;
        }
    }
    .body {
        margin-top: -8px;
    }
    .rule-container {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}
.modal-rules-content {
    display: flex;
    flex-direction: column;
    max-width: 500px;
    padding-inline: 12px;
    .head {
        margin-top: 8px;
        display: flex;
        align-items: center;
        gap: 4px;
        .circle-1 {
            width: 5px;
            height: 5px;
            border-radius: 100%;
            background-color: usePalette($p-blue, $p-200);
            margin-left: -6.5px;
        }
        .circle-2 {
            width: 5px;
            height: 5px;
            border-radius: 100%;
            background-color: usePalette($p-blue, $p-300);
        }
    }
}
.footer-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
</style>
